import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, pipe, EMPTY } from 'rxjs';
import { map, expand, tap, startWith, takeWhile, reduce } from 'rxjs/operators';

import { Envelope } from '@app/utils/flume-api.interceptor';
import { Page } from '@app/models/page';
import { Query } from '@app/models/queries/queries.model';
import { config } from '@config/config';
import { AuthState } from '@app/auth/auth.state';
import { EnvelopeUtils } from '@app/utils/envelope.utils';
import { Model } from '@app/models/model';

type Constructor<T> = new (...args: any[]) => T;

/**
 * Defines a common interface for handling and caching http requests.
 * Url, params, and page are from the final form of the request.
 * Req is an observable that will exec the request and return the raw envelope response.
 * Models and model functions, when passed a model constructor, will extract one or
 * all the data results, construct new models, and return those.
 */
export interface Resolver<T extends Model> {
    url: string | string[];
    params?: { [param: string]: string | boolean };
    page?: Page;
    req: Observable<Envelope<{ [key: string]: any }>>;
    models: (modelClass: Constructor<T>) => Observable<T[]>;
    model: (modelClass: Constructor<T>) => Observable<T>;
}

@Injectable({
    providedIn: 'root',
})
export class Fetch {
    constructor(protected http: HttpClient, protected authState: AuthState) {}

    /**
     * Makes a single request and returns the API envelope
     */
    get<T extends Model>(url: string): Resolver<T> {
        const fullUrl = `${this.constructUrl(url)}`;
        const req = this.http.get<Envelope<T>>(fullUrl);
        return {
            url: fullUrl,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) =>
                req.pipe(
                    map((res) => res.data.map((m) => new modelClass(m))),
                    EnvelopeUtils.handleError()
                ),
            model: (modelClass) =>
                req.pipe(
                    map((res) => new modelClass(res.data[0])),
                    EnvelopeUtils.handleError()
                ),
        };
    }

    /**
     * Makes a single request and returns the API envelope
     */
    getOne<T extends Model>(url: string, id: number | string): Resolver<T> {
        const fullUrl = `${this.constructUrl(url)}/${id}`;
        const req = this.http.get<Envelope<T>>(fullUrl);
        return {
            url: fullUrl,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) =>
                req.pipe(
                    map((res) => res.data.map((m) => new modelClass(m))),
                    EnvelopeUtils.handleError()
                ),
            model: (modelClass) =>
                req.pipe(
                    map((res) => new modelClass(res.data[0])),
                    EnvelopeUtils.handleError()
                ),
        };
    }

    /**
     * Makes multiple individual requests and strings together the responses
     * by taking the first API envelope, appending the data from every
     * response after, then returning that initial envelope with all the data.
     */
    getSome<T extends Model>(url: string, ids: (number | string)[]): Resolver<T> {
        const fullUrls = ids.map((id) => `${this.constructUrl(url)}/${id}`);
        const requests: Array<Observable<Envelope<T>>> = [];
        for (const u of fullUrls) {
            requests.push(this.http.get<Envelope<T>>(u));
        }
        const req = forkJoin(requests).pipe(
            map((envelopes) =>
                envelopes.reduce((acc, val) => {
                    acc.data = acc.data.concat(val.data);
                    return acc;
                }, envelopes[0])
            )
        );

        return {
            url: fullUrls,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) =>
                req.pipe(
                    map((res) => res.data.map((m) => new modelClass(m))),
                    EnvelopeUtils.handleError()
                ),
            model: (modelClass) =>
                req.pipe(
                    map((res) => new modelClass(res.data[0])),
                    EnvelopeUtils.handleError()
                ),
        };
    }

    /**
     * Fetches a collection from the API. Limit and offset are calculated from params.
     * @param page The page number to retrieve
     * @param options Any options that are passed as query params
     * but if this param is specified, it would be /utilities/:id/:path
     */
    getPage<T extends Model>(url: string, page: Page, options?: any): Resolver<T> {
        const fullUrl = this.constructUrl(url);
        const params = {
            limit: page.limit,
            offset: page.offset,
            ...options,
        };
        if (page.sortField && page.sortDirection) {
            params.sort_field = page.sortField;
            params.sort_direction = page.sortDirection;
        }
        const req = this.http.get<Envelope<T>>(fullUrl, { params });

        return {
            url: fullUrl,
            params,
            page,
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) =>
                req.pipe(
                    map((res) =>
                        res.data.map((m) => {
                            const mod = new modelClass(m);
                            mod.setTotalCount(res.count);
                            return mod;
                        })
                    ),
                    EnvelopeUtils.handleError()
                ),
            model: (modelClass) =>
                req.pipe(
                    map((res) => {
                        const mod = new modelClass(res.data[0]);
                        mod.setTotalCount(res.count);
                        return mod;
                    }),
                    EnvelopeUtils.handleError()
                ),
        };
    }

    /**
     * Fetches all models from the API. Recursively fetches pages of the
     * maximum size and appends them together into one envelope.
     * @param options Any options that are passed as query params
     */
    getAll<T extends Model>(url: string, options?: any): Resolver<T> {
        const fullUrl = this.constructUrl(url);
        const page = new Page(1, config.maxPageSize);
        const req = this.getPage('/' + fullUrl, page, options).req.pipe(
            expand((data: Envelope<T>) => {
                if (data.pagination && data.pagination.next) {
                    const nextPage = Page.fromUrl(data.pagination.next);
                    return this.getPage('/' + fullUrl, nextPage, options).req as Observable<Envelope<T>>;
                }
                return EMPTY.pipe(startWith(null)); // tslint:disable-line TODO look into the deprecation and update
            }),
            takeWhile((val) => val !== null),
            reduce((acc: Envelope<T>, curr: Envelope<T>) => {
                acc.data = acc.data.concat(curr.data);
                return acc;
            })
        );
        return {
            url: fullUrl,
            params: {
                all: true,
                ...options,
            },
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) =>
                req.pipe(
                    map((res) =>
                        res.data.map((m) => {
                            const mod = new modelClass(m);
                            mod.setTotalCount(res.count);
                            return mod;
                        })
                    ),
                    EnvelopeUtils.handleError()
                ),
            model: (modelClass) =>
                req.pipe(
                    map((res) => {
                        const mod = new modelClass(res.data[0]);
                        mod.setTotalCount(res.count);
                        return mod;
                    }),
                    EnvelopeUtils.handleError()
                ),
        };
    }

    post<T extends Model>(url: string, body: Partial<T>): Observable<Envelope<any>> {
        const fullUrl = this.constructUrl(url);
        return this.http.post<Envelope<any>>(fullUrl, body).pipe(
            map((res) => {
                res.message = EnvelopeUtils.message(res);
                return res;
            }),
            EnvelopeUtils.handleError()
        );
    }

    patch<T extends Model>(url: string, id: number | string, body: Partial<T>): Observable<Envelope<any>> {
        const fullUrl = `${this.constructUrl(url)}/${id}`;
        return this.http.patch<Envelope<any>>(fullUrl, body).pipe(
            map((res) => {
                res.message = EnvelopeUtils.message(res);
                return res;
            }),
            EnvelopeUtils.handleError()
        );
    }

    delete<T extends Model>(url: string, id: number | string): Observable<Envelope<any>> {
        const fullUrl = `${this.constructUrl(url)}/${id}`;
        return this.http.delete<Envelope<any>>(fullUrl).pipe(
            map((res) => {
                res.message = EnvelopeUtils.message(res);
                return res;
            }),
            EnvelopeUtils.handleError()
        );
    }

    /**
     * Fetches one or more queries, combining them efficiently to reduce network requests.
     */
    query(queries: Query[]): Resolver<Query> {
        const batchedByDevice: Record<string, Query[]> = {};

        // Group queries by device id but different query id
        for (let i = 0; i < queries.length; i++) {
            if (!(queries[i] instanceof Query)) {
                queries[i] = new Query(queries[i]);
            }
            const query = queries[i];
            if (batchedByDevice[query.device_id]) {
                batchedByDevice[query.device_id].push(query);
            } else {
                batchedByDevice[query.device_id] = [query];
            }
        }

        let requests: Observable<Envelope<Query>>[] = [];

        requests = Object.values(batchedByDevice).reduce((acc: Observable<Envelope<any>>[], batch) => {
            const httpReqs: Observable<Envelope<any>>[] = [];

            for (let i = 0; i < batch.length; i += config.maxQueries) {
                httpReqs.push(
                    this.http.post<Envelope<any>>(this.constructUrl(`/devices/${batch[0].device_id}/query`), {
                        queries: batch.slice(i, i + config.maxQueries).map((query) => query.toRequestBody()),
                    })
                );
            }

            return acc.concat(httpReqs);
        }, []);
        const req = forkJoin(requests).pipe(this.setDataPipe(queries));

        return {
            url: '',
            req: req.pipe(EnvelopeUtils.handleError()),
            models: (modelClass) =>
                req.pipe(
                    map((res) => res.data),
                    EnvelopeUtils.handleError()
                ),
            model: (modelClass) =>
                req.pipe(
                    map((res) => res.data[0]),
                    EnvelopeUtils.handleError()
                ),
        };
    }

    private setDataPipe(queries: Query[]) {
        return pipe(
            map((entries: Envelope<Record<string, any>>[]) => {
                const combinedData = entries.reduce((a, entry) => Object.assign(a, entry.data[0]), {});

                for (const key of Object.keys(combinedData)) {
                    const oldQuery = queries.find((query) => query.id === key);

                    oldQuery.setData(combinedData[key]);
                }

                return {
                    success: true,
                    code: 0,
                    message: '',
                    http_code: 200,
                    http_message: '',
                    data: queries,
                    count: 0,
                };
            })
        );
    }

    // If url begins with //, don't prepend the base url
    private constructUrl(url: string): string {
        if (url.slice(0, 2) === '//') {
            return url.slice(1);
        }
        return `${this.authState.baseURL()}${url}`;
    }
}
